/* eslint-disable no-param-reassign */
/* eslint-disable import/no-cycle */
import { createSlice } from '@reduxjs/toolkit';
import {
  TRY_LOGIN,
  LOGIN_SUCCESS,
  SIGNUP_SUCCESS,
  LOGIN_ERROR,
  LOGIN_ALERT,
  CONFIRM_LOGIN,
  LOG_OUT,
  CLEAR_LOGIN_ERROR,
  SIGNUP_REQUEST,
  SET_MAINTENANCE_MODE,
  CLEAR_MAINTENANCE_MODE,
  SET_PASSWORD_RESET_REQUIRED,
  CLEAR_PASSWORD_RESET_REQUIRED,
  SET_SESSION_EXPIRED,
  CLEAR_SESSION_EXPIRED,
  SESSION_TOKENS_REFRESHED,
} from '../constants/actionTypes';
import {
  OAUTH2_EXCHANGE_CODE_URL,
  HUBSPOT_FORM_SUBMIT,
  AUTH_LOGIN_URL,
  AUTH_ME_URL,
} from '../constants/apiUrls';
import { getWorkspaceFromUrl } from '../util/workspace';
import { fetchAccountInfo, fetchAccountQuota } from './Settings.slice';
import { SIGNIN_FORM_ID_PROD, PORTAL_ID_PROD } from '../constants/hubspotFormData';
import configData from '../config/loader';
import arClient from '../util/api_client';
import { initCsrfToken, setCsrfHeaderOnXhr } from '../util/csrf';

const initialState = {
  authenticating: false,
  authorized: false,
  loginErrorMessage: '',
  loginAlert: '',
  loginStatus: '',
  session: {},
  accountId: null,
  first: '',
  last: '',
  email: '',
  signup: false,
  maintenance: null,
  isMaintenanceMode: false,
  passwordResetRequired: false,
  passwordResetMessage: null,
  sessionExpired: false,
  workspaceData: null,
  authMe: null,
};

const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    setWorkspaceData(state, action) {
      state.workspaceData = action.payload ?? null;
    },
    setAuthMe(state, action) {
      state.authMe = action.payload ?? null;
    },
    setMaintenanceMode(state, action) {
      state.isMaintenanceMode = true;
      state.maintenance = action.payload;
    },
    clearMaintenanceMode(state) {
      state.isMaintenanceMode = false;
      state.maintenance = null;
    },
    setPasswordResetRequired(state, action) {
      state.passwordResetRequired = true;
      state.passwordResetMessage = action.payload?.message || 'Password update required';
    },
    clearPasswordResetRequired(state) {
      state.passwordResetRequired = false;
      state.passwordResetMessage = null;
    },
    setSessionExpired(state) {
      state.sessionExpired = true;
    },
    clearSessionExpired(state) {
      state.sessionExpired = false;
    },
  },
  extraReducers: (builder) => {
    // Handle legacy action type strings during migration
    builder
      .addCase(TRY_LOGIN, (state, action) => {
        state.authenticating = true;
        state.signup = true;
        state.session = { ...state.session, email: action.email };
      })
      .addCase(LOGIN_SUCCESS, (state, action) => {
        state.authenticating = false;
        state.authorized = true;
        state.session = { ...action.session, email: action.email };
        state.workspaceData = action.workspaceData ?? null;
      })
      .addCase(SIGNUP_SUCCESS, (state) => {
        state.signup = true;
        state.first = '';
        state.last = '';
        state.email = '';
      })
      .addCase(CONFIRM_LOGIN, (state, action) => {
        state.authenticating = false;
        state.authorized = true;
        state.session = action.session;
        if (action.session?.workspace != null) {
          state.workspaceData = action.session.workspace;
        }
      })
      .addCase(SESSION_TOKENS_REFRESHED, (state, action) => {
        // Only update a live session - a refresh resolving after logout must not resurrect it
        if (state.session?.access_token) {
          state.session = { ...state.session, ...action.tokens };
        }
      })
      .addCase(LOGIN_ERROR, (state, action) => {
        state.authenticating = false;
        state.authorized = false;
        state.loginErrorMessage = action.response.data;
        state.loginStatus = action.response.status;
        state.email = '';
        state.session = {};
      })
      .addCase(LOGIN_ALERT, (state, action) => {
        state.authenticating = false;
        state.authorized = false;
        state.loginAlert = action.response.data;
        state.loginStatus = action.response.status;
        state.email = '';
        state.session = {};
      })
      .addCase(LOG_OUT, () => initialState)
      .addCase(CLEAR_LOGIN_ERROR, (state) => {
        state.loginErrorMessage = '';
        state.loginAlert = '';
      })
      .addCase(SIGNUP_REQUEST, (state, action) => {
        state.first = action.data.first;
        state.last = action.data.last;
        state.email = action.data.email;
      })
      .addCase(SET_MAINTENANCE_MODE, (state, action) => {
        state.isMaintenanceMode = true;
        state.maintenance = action.payload;
      })
      .addCase(CLEAR_MAINTENANCE_MODE, (state) => {
        state.isMaintenanceMode = false;
        state.maintenance = null;
      })
      .addCase(SET_PASSWORD_RESET_REQUIRED, (state, action) => {
        state.passwordResetRequired = true;
        state.passwordResetMessage = action.payload?.message || 'Password update required';
      })
      .addCase(CLEAR_PASSWORD_RESET_REQUIRED, (state) => {
        state.passwordResetRequired = false;
        state.passwordResetMessage = null;
      })
      .addCase(SET_SESSION_EXPIRED, (state) => {
        state.sessionExpired = true;
      })
      .addCase(CLEAR_SESSION_EXPIRED, (state) => {
        state.sessionExpired = false;
      });
  },
});

export const {
  setWorkspaceData,
  setAuthMe,
  setMaintenanceMode,
  clearMaintenanceMode,
  setPasswordResetRequired,
  clearPasswordResetRequired,
  setSessionExpired,
  clearSessionExpired,
} = loginSlice.actions;

export default loginSlice.reducer;

// ============================================================================
// Action creators (plain actions dispatched by thunks)
// ============================================================================

export function attemptLogin(email) {
  return { type: TRY_LOGIN, email };
}

export function loginSuccessful(response, email, accountData, workspaceData = null) {
  return { type: LOGIN_SUCCESS, session: response, email, accountData, workspaceData };
}

export function trySignup(data) {
  return { type: SIGNUP_REQUEST, data };
}

export function signupSuccessful() {
  return { type: SIGNUP_SUCCESS };
}

export function confirmLogin(session) {
  return { type: CONFIRM_LOGIN, session };
}

export function loginUnsuccessful(response) {
  return { type: LOGIN_ERROR, response };
}

export function loginMessage(response) {
  return { type: LOGIN_ALERT, response };
}

export function logOut() {
  return { type: LOG_OUT };
}

export function clearLoginError() {
  return { type: CLEAR_LOGIN_ERROR };
}

// ============================================================================
// Thunks — Login
// ============================================================================

export function hubspotFormSubmission(
  portalId,
  formId,
  formType = 'signIn',
  formData = {},
  referrer = '',
  hutk = null
) {
  return () => {
    arClient.doPublicApiCall(
      HUBSPOT_FORM_SUBMIT,
      {
        portalId,
        formId,
        formType,
        formData: JSON.stringify(formData),
        referrer,
        hutk,
      },
      () => {
        // Response handled silently
      }
    );
  };
}

export function fetchAuthMe(callback) {
  return (_dispatch, getState) => {
    const sessionWs = getState()?.login?.session?.workspace?.name;
    const workspaceHint = getWorkspaceFromUrl() || sessionWs || null;
    const headers = { 'X-Tenant': configData.tenantName };
    if (workspaceHint) {
      headers['X-Workspace'] = workspaceHint;
    }
    arClient.doApiCallGet(
      AUTH_ME_URL,
      (data) => {
        const failed = !data || data.status === 'ERROR' || data.error === 'Authentication required';
        if (failed) {
          _dispatch(setAuthMe(null));
        } else {
          _dispatch(setAuthMe(data));
        }
        const workspaceData = failed ? null : data.workspace ?? data.data?.workspace ?? null;
        callback(workspaceData);
      },
      { headers }
    );
  };
}

export function syncWorkspaceFromAuthMe() {
  return (dispatch) => {
    dispatch(
      fetchAuthMe((workspaceData) => {
        dispatch(setWorkspaceData(workspaceData));
      })
    );
  };
}

export function sendLogin(email, password, signup = false, workspaceOverride = null) {
  return (dispatch) =>
    new Promise((resolve, reject) => {
      // SECURITY: Clear any previous user's data before attempting new login
      dispatch(logOut());
      dispatch(attemptLogin(email));

      const workspace = workspaceOverride ?? getWorkspaceFromUrl();

      initCsrfToken().then(() => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', AUTH_LOGIN_URL, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('X-Tenant', configData.tenantName);
        if (workspace) {
          xhr.setRequestHeader('X-Workspace', workspace);
        }
        xhr.withCredentials = true;
        setCsrfHeaderOnXhr(xhr);

        const body = JSON.stringify({
          email,
          password,
          ...(workspace ? { workspace } : {}),
        });
        xhr.send(body);

        xhr.onreadystatechange = () => {
          if (xhr.readyState !== 4) return;

          // Handle email not verified (403)
          if (xhr.status === 403) {
            const errorResponse = JSON.parse(xhr.responseText || '{}');
            if (errorResponse.error === 'email_not_verified') {
              dispatch(
                loginUnsuccessful({
                  status: 403,
                  data:
                    errorResponse.error_description ||
                    'Please verify your email address before logging in.',
                })
              );
              reject(
                new Error(
                  errorResponse.error_description ||
                  'Please verify your email address before logging in.'
                )
              );
              return;
            }
            dispatch(
              loginUnsuccessful({
                status: 403,
                data: errorResponse.error_description || 'Access denied.',
              })
            );
            reject(new Error(errorResponse.error_description || 'Access denied.'));
            return;
          }

      // Handle validation errors (422) - includes workspace mismatch
      if (xhr.status === 422) {
        const errorResponse = JSON.parse(xhr.responseText);
        const errors = errorResponse.errors || {};
        const firstError = Object.values(errors)[0];
        const errorMessage = Array.isArray(firstError)
          ? firstError[0]
          : errorResponse.error_description || 'Validation failed.';
        dispatch(
          loginUnsuccessful({
            status: 422,
            data: errorMessage,
          })
        );
        return;
      }

      if (xhr.status === 401) {
        const errorResponse = JSON.parse(xhr.responseText);
        dispatch(
          loginUnsuccessful({
            status: 401,
            data: errorResponse.error_description || 'Invalid credentials.',
          })
        );
        return;
      }

      if (xhr.status !== 200) {
        const errorResponse = JSON.parse(xhr.responseText);
        dispatch(
          loginUnsuccessful({
            status: xhr.status,
            data: errorResponse.error_description || 'Login failed. Please try again.',
          })
        );
        return;
      }

          const response = JSON.parse(xhr.responseText || '{}');

          if (response.error) {
            dispatch(
              loginUnsuccessful({
                status: 0,
                data: response.error_description || response.error,
              })
            );
            reject(new Error(response.error_description || response.error));
            return;
          }

          if (!response.access_token) {
            dispatch(
              loginUnsuccessful({
                status: 0,
                data: 'Invalid response from server.',
              })
            );
            reject(new Error('Invalid response from server.'));
            return;
          }

          if (signup === true) {
            dispatch(signupSuccessful());
          }

          // Initialize API client with Sanctum tokens
          arClient.initOAuth2(response, () => {
            dispatch(
              fetchAccountInfo(response.access_token, response.account_id, (accountData) => {
                const referrerMap = {
                  LICKLY: 'lickly.com',
                  'RAD INTEL': 'radintel.ai',
                };
                const referrer = referrerMap[configData.companyName] || 'other';

                dispatch(
                  hubspotFormSubmission(
                    PORTAL_ID_PROD,
                    SIGNIN_FORM_ID_PROD,
                    'signIn',
                    { email },
                    referrer
                  )
                );

                dispatch(fetchAccountQuota(response.access_token, response.account_id));

                const sessionData = {
                  account_id: response.account_id,
                  access_token: response.access_token,
                  refresh_token: response.refresh_token,
                  expires_in: response.expires_in,
                  token_type: 'Bearer',
                  expires_at: Date.now() + response.expires_in * 1000,
                  workspace: response.workspace,
                };

                dispatch(
                  fetchAuthMe((workspaceData) => {
                    dispatch(loginSuccessful(sessionData, email, accountData, workspaceData));
                    resolve({ success: true });
                  })
                );
              })
            );
          });
        };
      });
    });
}

// ============================================================================
// Thunks — Autologin
// ============================================================================

/**
 * Secure auto-login using one-time magic code.
 * Code is exchanged for tokens server-side.
 */
export function sendAutoLoginWithCode(code) {
  return (dispatch) => {
    // SECURITY: Clear previous user's data before logging in new user
    dispatch(logOut());

    const xhr = new XMLHttpRequest();
    xhr.open('POST', OAUTH2_EXCHANGE_CODE_URL, true);
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    xhr.setRequestHeader('X-Tenant', configData.tenantName);
    xhr.withCredentials = true;

    const body = new URLSearchParams({ code }).toString();
    xhr.send(body);

    xhr.onreadystatechange = () => {
      if (xhr.readyState !== 4) return;

      if (xhr.status !== 200) {
        const errorResponse =
          xhr.status === 0 ? { error_description: 'Network error' } : JSON.parse(xhr.responseText);
        dispatch(
          loginUnsuccessful({
            status: xhr.status,
            data:
              errorResponse.error_description ||
              'Auto-login failed. Link may be expired or already used.',
          })
        );
        return;
      }

      const response = JSON.parse(xhr.responseText);

      if (response.error) {
        dispatch(
          loginUnsuccessful({
            status: 400,
            data: response.error_description || 'Invalid magic link',
          })
        );
        return;
      }

      arClient.initOAuth2(response, () => {
        dispatch(
          fetchAccountInfo(response.access_token, response.account_id, (accountData) => {
            dispatch(
              hubspotFormSubmission(PORTAL_ID_PROD, SIGNIN_FORM_ID_PROD, 'signIn', {
                email: accountData.data.email,
              })
            );
            dispatch(
              loginSuccessful(
                {
                  accountId: response.account_id,
                  account_id: response.account_id,
                  consumer_key: response.access_token,
                  consumer_secret: response.account_id,
                  access_token: response.access_token,
                  refresh_token: response.refresh_token,
                  expires_in: response.expires_in,
                  expires_at: Date.now() + response.expires_in * 1000,
                },
                accountData.data.email,
                accountData
              )
            );
          })
        );
      });
    };
  };
}

/**
 * Legacy auto-login using direct tokens (deprecated - insecure).
 * Kept for backward compatibility with old links.
 */
export function sendAutoLogin(consumerKey, consumerSecret) {
  return (dispatch) => {
    // SECURITY: Clear previous user's data before logging in new user
    dispatch(logOut());

    // Check if this looks like a Sanctum/OAuth 2.0 token
    const isSanctumToken = consumerKey && (consumerKey.includes('|') || consumerKey.length > 40);

    if (isSanctumToken) {
      const tokens = {
        access_token: consumerKey,
        account_id: consumerSecret,
        expires_in: 900,
      };

      arClient.initOAuth2(tokens, () => {
        dispatch(
          fetchAccountInfo(consumerKey, consumerSecret, (accountData) => {
            dispatch(
              hubspotFormSubmission(PORTAL_ID_PROD, SIGNIN_FORM_ID_PROD, 'signIn', {
                email: accountData.data.email,
              })
            );
            dispatch(
              loginSuccessful(
                {
                  accountId: accountData.data.id,
                  consumer_key: consumerKey,
                  consumer_secret: consumerSecret,
                  access_token: consumerKey,
                },
                accountData.data.email,
                accountData
              )
            );
          })
        );
      });
    } else {
      // Legacy OAuth 1.0 flow for old-style tokens
      arClient.initV2(
        consumerKey,
        consumerSecret,
        () => {
          dispatch(
            fetchAccountInfo(consumerKey, consumerSecret, (accountData) => {
              dispatch(
                hubspotFormSubmission(PORTAL_ID_PROD, SIGNIN_FORM_ID_PROD, 'signIn', {
                  email: accountData.data.email,
                })
              );
              dispatch(
                loginSuccessful(
                  {
                    accountId: accountData.data.id,
                    consumer_key: consumerKey,
                    consumer_secret: consumerSecret,
                  },
                  accountData.data.email,
                  accountData
                )
              );
            })
          );
        },
        (error) => {
          dispatch(
            loginUnsuccessful({ status: 401, data: error.statusText || 'Auto-login failed' })
          );
        }
      );
    }
  };
}
